import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Declare gtag function globally
declare global {
  interface Window {
    gtag: (
      command: 'config' | 'event' | 'js' | 'consent',
      targetId: string,
      config?: Record<string, any>
    ) => void;
    dataLayer: any[];
  }
}

// Google Analytics Tracking ID
const GA_TRACKING_ID = 'G-1LEHSJ9N5B';

// Enhanced Google Analytics Hook
export const useGoogleAnalytics = () => {
  const location = useLocation();

  // Track page views on route changes
  useEffect(() => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: document.title,
        page_location: window.location.href,
        page_path: location.pathname + location.search
      });
    }
  }, [location]);

  // Track custom events
  const trackEvent = (
    action: string,
    category: string,
    label?: string,
    value?: number
  ) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', action, {
        event_category: category,
        event_label: label,
        value: value
      });
    }
  };

  // Track form submissions
  const trackFormSubmit = (formName: string, formType: string) => {
    trackEvent('form_submit', 'engagement', formName);
    
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'form_submit', {
        form_name: formName,
        form_type: formType,
        event_category: 'engagement'
      });
    }
  };

  // Track course interactions
  const trackCourseInteraction = (
    action: 'view' | 'enroll' | 'download_brochure' | 'syllabus_download',
    courseName: string,
    coursePrice?: number
  ) => {
    if (typeof window !== 'undefined' && window.gtag) {
      let eventName = 'view_item';
      if (action === 'enroll') {
        eventName = 'begin_checkout';
      } else if (action === 'syllabus_download') {
        eventName = 'download';
      }
      
      window.gtag('event', eventName, {
        currency: 'INR',
        value: coursePrice,
        items: [{
          item_id: courseName.toLowerCase().replace(/\s+/g, '_'),
          item_name: courseName,
          price: coursePrice,
          quantity: 1,
          category: 'course'
        }],
        event_category: 'course_interaction',
        event_label: courseName
      });
    }
  };

  // Track phone number clicks
  const trackPhoneClick = (phoneNumber: string, location: string) => {
    trackEvent('phone_click', 'contact', `${location}_${phoneNumber}`);
  };

  // Track email clicks
  const trackEmailClick = (email: string, location: string) => {
    trackEvent('email_click', 'contact', `${location}_${email}`);
  };

  // Track WhatsApp clicks
  const trackWhatsAppClick = (location: string) => {
    trackEvent('whatsapp_click', 'contact', location);
  };

  // Track social media clicks
  const trackSocialClick = (platform: string, location: string) => {
    trackEvent('social_click', 'social_media', `${platform}_${location}`);
  };

  // Track download events
  const trackDownload = (fileName: string, fileType: string) => {
    trackEvent('download', 'engagement', `${fileName}_${fileType}`);
  };

  // Track scroll depth
  const trackScrollDepth = (depth: number) => {
    if (depth === 25 || depth === 50 || depth === 75 || depth === 90) {
      trackEvent('scroll_depth', 'engagement', `${depth}%`);
    }
  };

  // Track time on page
  const trackTimeOnPage = (seconds: number) => {
    if (seconds >= 30) {
      trackEvent('time_on_page', 'engagement', `${Math.floor(seconds / 30) * 30}s`);
    }
  };

  // Track error events
  const trackError = (errorMessage: string, errorType: string) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'exception', {
        description: errorMessage,
        fatal: false,
        event_category: 'error',
        event_label: errorType
      });
    }
  };

  return {
    trackEvent,
    trackFormSubmit,
    trackCourseInteraction,
    trackPhoneClick,
    trackEmailClick,
    trackWhatsAppClick,
    trackSocialClick,
    trackDownload,
    trackScrollDepth,
    trackTimeOnPage,
    trackError
  };
};

// Initialize Google Analytics
export const initializeGA = () => {
  if (typeof window !== 'undefined') {
    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];
    
    // Define gtag function
    function gtag(...args: any[]) {
      window.dataLayer.push(args);
    }
    
    // Set gtag on window
    window.gtag = gtag;
    
    // Initialize with tracking ID
    gtag('js', new Date());
    gtag('config', GA_TRACKING_ID, {
      page_title: document.title,
      page_location: window.location.href,
      send_page_view: true
    });
  }
};
